import { caseApiRequest } from './apiClient';
import type { ApiResult } from './types';
import { isCaseRequestUuid } from '../pages/task-case/taskCaseUpdateHelpers';

export type WorkflowTaskSubsidiaryStatusResponse = {
  has_blocking_sub_tasks?: boolean;
  hasBlockingSubTasks?: boolean;
};

export type TimeUnitRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
};

export type SubsidiaryTaskOptionRow = {
  id: string;
  code: string;
  name: string;
  description: string | null;
  active: boolean;
};

export type CaseRequestSubsidiaryTaskRow = {
  subsidiaryTaskId?: string;
  subsidiaryTask: string;
  description: string;
  assignedTo: string;
  dueDate: string;
  workflowStage: string;
  createdAt: string;
  completedAt: string | null;
  cancelledAt: string | null;
  showCancelButton?: boolean;
  status: string;
  comment?: string;
};

/** CMS-CRST-2 — task row for the logged-in assignee (Junior Counsel view). */
export type AssignedUserSubsidiaryTaskRow = {
  subsidiaryTaskId: string;
  subsidiaryTask: string;
  description: string;
  assignedTo: string;
  dueDate: string;
  workflowStage: string;
  createdAt: string;
  completedAt: string | null;
  status: string;
  comment: string;
  showStartButton: boolean;
  showMarkCompleteButton: boolean;
  showCancelButton: boolean;
  fieldsDisabled: boolean;
};

export type CaseRequestSubsidiaryTasksResponse = {
  caseRequestId: string;
  totalCount: number;
  tasks: CaseRequestSubsidiaryTaskRow[];
};

export type AssignedUserSubsidiaryTasksResponse = {
  caseRequestId: string;
  totalCount: number;
  tasks: AssignedUserSubsidiaryTaskRow[];
};

export type SubTaskAssigneeRow = {
  userId: string;
  displayName: string;
  designation: string;
  displayLabel: string;
};

export type CreateSubsidiaryTaskRequest = {
  subsidiaryTaskId: string;
  description: string;
  assignedUserIds: string[];
  ttc?: number;
  ttcTimeUnitId?: string;
  ttr?: number;
  ttrTimeUnitId?: string;
};

function asArray(raw: unknown): unknown[] {
  return Array.isArray(raw) ? raw : [];
}

function str(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function normalizeTimeUnits(raw: unknown): TimeUnitRow[] {
  return asArray(raw)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const id = str(o, 'id');
      const code = str(o, 'code');
      const name = str(o, 'name');
      if (!id || !name) return null;
      return {
        id,
        code,
        name,
        description: typeof o.description === 'string' ? o.description : null,
        active: typeof o.active === 'boolean' ? o.active : true,
      } satisfies TimeUnitRow;
    })
    .filter((x): x is TimeUnitRow => Boolean(x));
}

function normalizeSubsidiaryTaskOptions(raw: unknown): SubsidiaryTaskOptionRow[] {
  return asArray(raw)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const id = str(o, 'id');
      const code = str(o, 'code');
      const name = str(o, 'name');
      if (!id || !name) return null;
      return {
        id,
        code,
        name,
        description: typeof o.description === 'string' ? o.description : null,
        active: typeof o.active === 'boolean' ? o.active : true,
      } satisfies SubsidiaryTaskOptionRow;
    })
    .filter((x): x is SubsidiaryTaskOptionRow => Boolean(x));
}

function normalizeSubsidiaryTaskRow(row: Record<string, unknown>): CaseRequestSubsidiaryTaskRow {
  const subsidiaryTaskId = str(row, 'subsidiaryTaskId', 'subsidiary_task_id');
  return {
    ...(subsidiaryTaskId ? { subsidiaryTaskId } : {}),
    subsidiaryTask: str(row, 'subsidiaryTask', 'subsidiary_task'),
    description: str(row, 'description'),
    assignedTo: str(row, 'assignedTo', 'assigned_to'),
    dueDate: str(row, 'dueDate', 'due_date'),
    workflowStage: str(row, 'workflowStage', 'workflow_stage'),
    createdAt: str(row, 'createdAt', 'created_at'),
    completedAt: str(row, 'completedAt', 'completed_at') || null,
    cancelledAt: str(row, 'cancelledAt', 'cancelled_at') || null,
    showCancelButton: bool(row, 'showCancelButton', 'show_cancel_button') ?? false,
    status: str(row, 'status'),
    comment: str(row, 'comment'),
  };
}

function normalizeAssignedUserTaskRow(row: Record<string, unknown>): AssignedUserSubsidiaryTaskRow | null {
  const subsidiaryTaskId = str(row, 'subsidiaryTaskId', 'subsidiary_task_id');
  if (!subsidiaryTaskId) return null;
  return {
    subsidiaryTaskId,
    subsidiaryTask: str(row, 'subsidiaryTask', 'subsidiary_task'),
    description: str(row, 'description'),
    assignedTo: str(row, 'assignedTo', 'assigned_to'),
    dueDate: str(row, 'dueDate', 'due_date'),
    workflowStage: str(row, 'workflowStage', 'workflow_stage'),
    createdAt: str(row, 'createdAt', 'created_at'),
    completedAt: str(row, 'completedAt', 'completed_at') || null,
    status: str(row, 'status'),
    comment: str(row, 'comment'),
    showStartButton: bool(row, 'showStartButton', 'show_start_button') ?? false,
    showMarkCompleteButton: bool(row, 'showMarkCompleteButton', 'show_mark_complete_button') ?? false,
    showCancelButton: bool(row, 'showCancelButton', 'show_cancel_button') ?? false,
    fieldsDisabled: bool(row, 'fieldsDisabled', 'fields_disabled') ?? false,
  };
}

function normalizeCaseRequestTasks(raw: unknown): CaseRequestSubsidiaryTasksResponse {
  if (!raw || typeof raw !== 'object') {
    return { caseRequestId: '', totalCount: 0, tasks: [] };
  }
  const o = raw as Record<string, unknown>;
  const tasksRaw = o.tasks ?? (o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>).tasks : undefined);
  const tasks = asArray(tasksRaw)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      return normalizeSubsidiaryTaskRow(r as Record<string, unknown>);
    })
    .filter((x): x is CaseRequestSubsidiaryTaskRow => Boolean(x));

  const caseRequestId = str(o, 'caseRequestId', 'case_request_id');
  const totalCountRaw = o.totalCount ?? (o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>).totalCount : undefined);
  const totalCount = typeof totalCountRaw === 'number' ? totalCountRaw : tasks.length;
  return { caseRequestId, totalCount, tasks };
}

function normalizeAssignedUserTasks(raw: unknown): AssignedUserSubsidiaryTasksResponse {
  if (!raw || typeof raw !== 'object') {
    return { caseRequestId: '', totalCount: 0, tasks: [] };
  }
  const o = raw as Record<string, unknown>;
  const tasksRaw = o.tasks ?? (o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>).tasks : undefined);
  const tasks = asArray(tasksRaw)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      return normalizeAssignedUserTaskRow(r as Record<string, unknown>);
    })
    .filter((x): x is AssignedUserSubsidiaryTaskRow => Boolean(x));

  const caseRequestId = str(o, 'caseRequestId', 'case_request_id');
  const totalCountRaw = o.totalCount ?? (o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>).totalCount : undefined);
  const totalCount = typeof totalCountRaw === 'number' ? totalCountRaw : tasks.length;
  return { caseRequestId, totalCount, tasks };
}

export async function listTimeUnits(input?: {
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<TimeUnitRow[]>> {
  // Swagger examples use applicationCode `w` for CMS-MTU-1.
  const applicationCode = (input?.applicationCode ?? import.meta.env.VITE_APP_CODE_TIME_UNITS ?? 'w').trim();
  const res = await caseApiRequest<unknown>('/time-units', {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeTimeUnits(res.data) };
}

function normalizeSubTaskAssignees(raw: unknown): SubTaskAssigneeRow[] {
  return asArray(raw)
    .map((r) => {
      if (!r || typeof r !== 'object') return null;
      const o = r as Record<string, unknown>;
      const userId = str(o, 'userId', 'user_id');
      if (!userId) return null;
      const displayName = str(o, 'displayName', 'display_name');
      const designation = str(o, 'designation');
      const displayLabel = str(o, 'displayLabel', 'display_label');
      return { userId, displayName, designation, displayLabel } satisfies SubTaskAssigneeRow;
    })
    .filter((x): x is SubTaskAssigneeRow => Boolean(x));
}

export function subTaskAssigneeLabel(row: SubTaskAssigneeRow): { id: string; label: string } {
  const displayLabel = row.displayLabel.trim();
  if (displayLabel) return { id: row.userId, label: displayLabel };

  const name = row.displayName.trim();
  const designation = row.designation.trim();
  const label = name && designation ? `${name} - ${designation}` : name || row.userId;
  return { id: row.userId, label };
}

/**
 * CMS-AUQ-10
 * GET /v1/case-requests/users/sub-task-assignees?search=...
 */
export async function listSubTaskAssignees(input?: {
  search?: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<SubTaskAssigneeRow[]>> {
  const applicationCode = (input?.applicationCode ?? import.meta.env.VITE_APP_CODE_SUB_TASK_ASSIGNEES ?? 'INTRANET').trim();
  if (!applicationCode) return { success: false, message: 'Missing application code.' };

  const search = input?.search?.trim();
  const path = search
    ? `/users/sub-task-assignees?search=${encodeURIComponent(search)}`
    : '/users/sub-task-assignees';

  const res = await caseApiRequest<unknown>(path, {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeSubTaskAssignees(res.data) };
}

export async function listSubsidiaryTaskOptions(input?: {
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<SubsidiaryTaskOptionRow[]>> {
  // Swagger examples use applicationCode `r` for CMS-MST-1.
  const applicationCode = (input?.applicationCode ?? import.meta.env.VITE_APP_CODE_SUBSIDIARY_TASKS ?? 'r').trim();
  const res = await caseApiRequest<unknown>('/subsidiary-tasks', {
    method: 'GET',
    signal: input?.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeSubsidiaryTaskOptions(res.data) };
}

export async function listCaseRequestSubsidiaryTasks(input: {
  caseRequestId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CaseRequestSubsidiaryTasksResponse>> {
  const id = input.caseRequestId.trim();
  if (!isCaseRequestUuid(id)) return { success: false, message: 'Invalid case request id.' };
  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_CASE_REQUEST_SUB_TASKS ?? 'INTRANET').trim();

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/subsidiary-tasks`, {
    method: 'GET',
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeCaseRequestTasks(res.data) };
}

export async function createCaseRequestSubsidiaryTask(input: {
  caseRequestId: string;
  body: CreateSubsidiaryTaskRequest;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<void>> {
  const id = input.caseRequestId.trim();
  if (!isCaseRequestUuid(id)) return { success: false, message: 'Invalid case request id.' };

  const assignedUserIds = input.body.assignedUserIds.map((userId) => userId.trim()).filter(Boolean);
  if (!assignedUserIds.length) return { success: false, message: 'Assign To is required.' };

  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_CREATE_SUB_TASK ?? 'INTRANET').trim();
  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/subsidiary-tasks`, {
    method: 'POST',
    body: { ...input.body, assignedUserIds },
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

/** CMS-CRST-2 — GET assigned subsidiary tasks for the logged-in user. */
export async function listAssignedUserSubsidiaryTasks(input: {
  caseRequestId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<AssignedUserSubsidiaryTasksResponse>> {
  const id = input.caseRequestId.trim();
  if (!isCaseRequestUuid(id)) return { success: false, message: 'Invalid case request id.' };
  const applicationCode = (
    input.applicationCode ?? import.meta.env.VITE_APP_CODE_ASSIGNED_USER_SUB_TASKS ?? 'INTRANET'
  ).trim();

  const res = await caseApiRequest<unknown>(`/${encodeURIComponent(id)}/assigned-user-subsidiary-tasks`, {
    method: 'GET',
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: normalizeAssignedUserTasks(res.data) };
}

/** CMS-CRST-3 — PATCH start subsidiary task. */
export async function startSubsidiaryTask(input: {
  subsidiaryTaskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<AssignedUserSubsidiaryTaskRow>> {
  const subsidiaryTaskId = input.subsidiaryTaskId.trim();
  if (!subsidiaryTaskId) return { success: false, message: 'Invalid subsidiary task id.' };
  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_START_SUB_TASK ?? 'INTRANET').trim();

  const res = await caseApiRequest<unknown>(`/subsidiary-tasks/start/${encodeURIComponent(subsidiaryTaskId)}`, {
    method: 'PATCH',
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };

  const d = res.data;
  if (d && typeof d === 'object') {
    const row = normalizeAssignedUserTaskRow(d as Record<string, unknown>);
    if (row) return { success: true, data: row };
  }
  return {
    success: true,
    data: {
      subsidiaryTaskId,
      subsidiaryTask: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      workflowStage: '',
      createdAt: '',
      completedAt: null,
      status: '',
      comment: '',
      showStartButton: false,
      showMarkCompleteButton: false,
      showCancelButton: false,
      fieldsDisabled: false,
    },
  };
}

/** CMS-CRST-4 — PATCH complete subsidiary task. */
export async function completeSubsidiaryTask(input: {
  subsidiaryTaskId: string;
  comment: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<AssignedUserSubsidiaryTaskRow>> {
  const subsidiaryTaskId = input.subsidiaryTaskId.trim();
  if (!subsidiaryTaskId) return { success: false, message: 'Invalid subsidiary task id.' };
  const applicationCode = (input.applicationCode ?? import.meta.env.VITE_APP_CODE_COMPLETE_SUB_TASK ?? 'INTRANET').trim();

  const res = await caseApiRequest<unknown>(`/subsidiary-tasks/complete/${encodeURIComponent(subsidiaryTaskId)}`, {
    method: 'PATCH',
    body: { comment: input.comment.trim() },
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };

  const d = res.data;
  if (d && typeof d === 'object') {
    const row = normalizeAssignedUserTaskRow(d as Record<string, unknown>);
    if (row) return { success: true, data: row };
  }
  return {
    success: true,
    data: {
      subsidiaryTaskId,
      subsidiaryTask: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      workflowStage: '',
      createdAt: '',
      completedAt: null,
      status: '',
      comment: input.comment.trim(),
      showStartButton: false,
      showMarkCompleteButton: false,
      showCancelButton: false,
      fieldsDisabled: false,
    },
  };
}

export type RevertSubsidiaryTaskRequest = {
  workflowInstanceStageUserTaskId: string;
  physicalFileMovementRequired: boolean;
  documentMoverId?: string;
};

/** CMS-CRST-7 — POST /v1/case-requests/subsidiary-tasks/revert */
export async function revertSubsidiaryTask(input: {
  body: RevertSubsidiaryTaskRequest;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<void>> {
  const workflowInstanceStageUserTaskId = input.body.workflowInstanceStageUserTaskId.trim();
  const documentMoverId = (input.body.documentMoverId ?? '').trim();

  if (!isCaseRequestUuid(workflowInstanceStageUserTaskId)) {
    return { success: false, message: 'Invalid user task id.' };
  }
  if (input.body.physicalFileMovementRequired && !documentMoverId) {
    return { success: false, message: 'Document mover is required.' };
  }

  const applicationCode = (
    input.applicationCode ?? import.meta.env.VITE_APP_CODE_REVERT_SUB_TASK ?? 'INTRANET'
  ).trim();

  const res = await caseApiRequest<unknown>('/subsidiary-tasks/revert', {
    method: 'POST',
    body: {
      workflow_instance_stage_user_task_id: workflowInstanceStageUserTaskId,
      physical_file_movement_required: input.body.physicalFileMovementRequired,
      document_mover_id: input.body.physicalFileMovementRequired ? documentMoverId : null,
    },
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
  });
  if (!res.ok) return { success: false, message: res.message };
  return { success: true, data: undefined };
}

export async function cancelSubsidiaryTask(input: {
  subsidiaryTaskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<CaseRequestSubsidiaryTaskRow>> {
  const subsidiaryTaskId = input.subsidiaryTaskId.trim();
  if (!subsidiaryTaskId) return { success: false, message: 'Invalid subsidiary task id.' };
  const applicationCode = (
    input.applicationCode ?? import.meta.env.VITE_APP_CODE_CANCEL_SUB_TASK ?? 'INTRANET'
  ).trim();

  const res = await caseApiRequest<unknown>(
    `/subsidiary-tasks/cancel/${encodeURIComponent(subsidiaryTaskId)}`,
    {
      method: 'PATCH',
      signal: input.signal,
      headers: { applicationCode, 'application-code': applicationCode },
    },
  );
  if (!res.ok) return { success: false, message: res.message };

  const d = res.data;
  if (d && typeof d === 'object') {
    return { success: true, data: normalizeSubsidiaryTaskRow(d as Record<string, unknown>) };
  }
  return {
    success: true,
    data: {
      subsidiaryTask: '',
      description: '',
      assignedTo: '',
      dueDate: '',
      workflowStage: '',
      createdAt: '',
      completedAt: null,
      cancelledAt: null,
      status: '',
    },
  };
}

function bool(o: Record<string, unknown>, ...keys: string[]): boolean | null {
  for (const k of keys) {
    const v = o[k];
    if (v === true || v === false) return v;
    if (v === 'true') return true;
    if (v === 'false') return false;
  }
  return null;
}

/**
 * CMS-CCA-8
 * POST /v1/case-requests/workflow-tasks/subsidiary-status
 *
 * Check whether a workflow task has blocking subsidiary tasks in PENDING or IN_PROGRESS status.
 */
export async function getWorkflowTaskSubsidiaryStatus(input: {
  taskId: string;
  applicationCode?: string;
  signal?: AbortSignal;
}): Promise<ApiResult<{ hasBlockingSubTasks: boolean }>> {
  const taskId = input.taskId.trim();
  if (!taskId) return { success: false, message: 'Invalid task id.' };

  const applicationCode = (
    input.applicationCode ?? import.meta.env.VITE_APP_CODE_SUBSIDIARY_STATUS ?? 'INTRANET'
  ).trim();

  const res = await caseApiRequest<unknown>('/workflow-tasks/subsidiary-status', {
    method: 'POST',
    signal: input.signal,
    headers: { applicationCode, 'application-code': applicationCode },
    body: { task_id: taskId },
  });

  if (!res.ok) return { success: false, message: res.message };

  const raw = res.data;
  if (raw && typeof raw === 'object') {
    const o = raw as Record<string, unknown>;
    // Some endpoints may wrap the payload as { data: { ... } }.
    const d =
      o.data && typeof o.data === 'object' ? (o.data as Record<string, unknown>) : (o as Record<string, unknown>);
    const v = bool(d, 'has_blocking_sub_tasks', 'hasBlockingSubTasks');
    if (v !== null) return { success: true, data: { hasBlockingSubTasks: v } };
  }

  return { success: false, message: 'Invalid subsidiary status response.' };
}

